import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';

import cardholderAddIcon from '../icons/tl_cardholderadd.svg';
import cardholderDeleteIcon from '../icons/tl_cardholderdelete.svg';
import lockIcon from '../icons/tl_lock.svg';
import reportIcon from '../icons/tl_report.svg';
import securityIcon from '../icons/tl_security.svg';
import unknownIcon from '../icons/tl_unknown.svg';

const useStyles = makeStyles((theme) => ({
  paper: {
    height: '100%',
    minWidth: theme.spacing(50),
    padding: theme.spacing(2),
    overflowY: 'auto',
  },
  title: {
    fontSize: theme.spacing(2.25),
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(3),
  },
  content: {
    display: 'flex',
  },
  icons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginRight: theme.spacing(2),
  },
  divider: {
    borderLeft: `1px solid #e9e9f0`,
    height: theme.spacing(4.5),
    margin: theme.spacing(0.4),
  },
  text: {
    fontSize: theme.spacing(1.625),
    color: theme.palette.primary.main,
    marginTop: theme.spacing(1.25),
  },
  underline: {
    textDecoration: 'underline',
  },
  time: {
    fontSize: theme.spacing(1.625),
    color: theme.palette.neutral.light,
  },
}));

const Timeline = () => {
  const classes = useStyles();

  const getIcon = (icon) => {
    switch (icon) {
      case 'CARDHOLDER_ADD':
        return <img src={cardholderAddIcon} alt="Cardholder added" />;
      case 'CARDHOLDER_DELETE':
        return <img src={cardholderDeleteIcon} alt="Cardholder deleted" />;
      case 'REPORT':
        return <img src={reportIcon} alt="Report run" />;
      case 'LOGIN':
        return <img src={lockIcon} alt="Login event" />;
      case 'SECURITY':
        return <img src={securityIcon} alt="Security event" />;
      default:
        return <img src={unknownIcon} alt="Unknown event" />;
    }
  };

  const cardholderAdd = ({ user, cardholder }) => {
    return (
      <Typography component="div" className={classes.text}>
        <Box fontWeight="fontWeightBold" display="inline">
          {user}
        </Box>{' '}
        added a new cardholder{' '}
        <Box className={classes.underline} fontWeight="fontWeightBold" display="inline">
          {cardholder}
        </Box>
        .
      </Typography>
    );
  };

  const reportRun = ({ subject }) => {
    return (
      <Typography component="div" className={classes.text}>
        A report was run for{' '}
        <Box className={classes.underline} fontWeight="fontWeightBold" display="inline">
          {subject}
        </Box>
        .
      </Typography>
    );
  };

  const newLogin = () => {
    return (
      <Typography component="div" className={classes.text}>
        {/* eslint-disable-next-line react/jsx-curly-brace-presence */}
        {`You've logged in on a new device.`}
      </Typography>
    );
  };

  const hardwareEdited = ({ name }) => {
    return (
      <Typography component="div" className={classes.text}>
        <Box className={classes.underline} fontWeight="fontWeightBold" display="inline">
          {name}
        </Box>{' '}
        was edited.
      </Typography>
    );
  };

  const clientConnection = ({ host }) => {
    return (
      <Typography component="div" className={classes.text}>
        A new Client connection was used from{' '}
        <Box fontWeight="fontWeightBold" display="inline">
          {host}
        </Box>
        .
      </Typography>
    );
  };

  const roleUpdate = ({ user }) => {
    return (
      <Typography component="div" className={classes.text}>
        <Box className={classes.underline} fontWeight="fontWeightBold" display="inline">
          {user}
        </Box>{' '}
        was added to the System Admin role.
      </Typography>
    );
  };

  const cardholderDelete = ({ cardholder }) => {
    return (
      <Typography component="div" className={classes.text}>
        <Box className={classes.underline} fontWeight="fontWeightBold" display="inline">
          {cardholder}
        </Box>{' '}
        cardholder record was deleted.
      </Typography>
    );
  };

  const moduleEnabled = ({ module }) => {
    return (
      <Typography component="div" className={classes.text}>
        A new{' '}
        <Box className={classes.underline} fontWeight="fontWeightBold" display="inline">
          {module}
        </Box>{' '}
        was enabled for PremiSys.
      </Typography>
    );
  };

  const events = [
    {
      method: cardholderAdd,
      data: { user: 'John Doe', cardholder: 'Jane Sample' },
      icon: 'CARDHOLDER_ADD',
      time: '4 hours ago',
    },
    { method: reportRun, data: { subject: 'Doors in Alarm' }, icon: 'REPORT', time: '8 hours ago' },
    { method: newLogin, icon: 'LOGIN', time: '2 days ago' },
    {
      method: hardwareEdited,
      data: { name: 'N.E. Main Door' },
      icon: 'SECURITY',
      time: '3 days ago',
    },
    {
      method: cardholderAdd,
      data: { user: 'Bob Sample', cardholder: 'John Sample' },
      icon: 'CARDHOLDER_ADD',
      time: '3 days ago',
    },
    { method: reportRun, data: { subject: 'Active Cards' }, icon: 'REPORT', time: '4 days ago' },
    { method: clientConnection, data: { host: '192.168.1.15' }, icon: 'LOGIN', time: '5 days ago' },
    { method: roleUpdate, data: { user: 'Bob Sample' }, icon: 'SECURITY', time: '10 days ago' },
    {
      method: cardholderDelete,
      data: { cardholder: 'Angel Adams' },
      icon: 'CARDHOLDER_DELETE',
      time: '10 days ago',
    },
    { method: reportRun, data: { subject: 'Past Alarms' }, icon: 'REPORT', time: '10 days ago' },
    {
      method: moduleEnabled,
      data: { module: 'plugin module' },
      icon: 'SECURITY',
      time: '10 days ago',
    },
  ];

  return (
    <Paper className={classes.paper} square>
      <Typography className={classes.title}>Timeline</Typography>
      {events.map((event, i) => (
        // TODO: find out if there is anything unique about timeline events that can be used as a key other than the array index
        // https://github.com/yannickcr/eslint-plugin-react/blob/master/docs/rules/no-array-index-key.md
        // eslint-disable-next-line react/no-array-index-key
        <div className={classes.content} key={i}>
          <div className={classes.icons}>
            {getIcon(event.icon)}
            {i < events.length - 1 && <div className={classes.divider} />}
          </div>
          <div>
            {event.method(event.data)}
            <Typography className={classes.time}>{event.time}</Typography>
          </div>
        </div>
      ))}
    </Paper>
  );
};

export default Timeline;
